import { SafeError, SafePromise } from '@benzinga/safe-await';
import { ExtendedSubscribable, Subscription } from '@benzinga/subscribable';
import { AuthenticationManager, Session } from '@benzinga/session';

import { UserRequest, UserRestfulEvent } from './request';
import {
  UserEditInfo,
  UserAvatar,
  UserAvatarPost,
  UserTraderSurvey,
  NotificationChannelsResponse,
  ToggleSubscriptionResponse,
  TOSAcceptance,
} from './entities';
import { User } from '@benzinga/session';
import { deepEqual, oneRequestAtATime } from '@benzinga/utils';
import { UserStore, UserStoreEvent } from './store';

interface UserFunctions {
  updateAvatar: UserManager['updateAvatar'];
  editUser: UserManager['editUser'];
  getNotificationChannels: UserManager['getNotificationChannels'];
  toggleSubscription: UserManager['toggleSubscription'];
}

interface UserUpdateEvent {
  type: 'user:user_update';
  user: User | undefined;
}

export type UserManagerEvent = UserRestfulEvent | UserUpdateEvent | UserStoreEvent;

export class UserManager extends ExtendedSubscribable<UserManagerEvent, UserFunctions> {
  private session: Session;
  private store: UserStore;
  private request: UserRequest;
  private user?: User;
  private requestSubscription?: Subscription<UserRequest>;
  private storeSubscription?: Subscription<UserStore>;
  private fetchGlobalSettings = oneRequestAtATime(async (): SafePromise<object> => {
    const val = await this.request.getGlobalSettings();
    if (val.err) {
      return val;
    } else {
      this.store.setGlobalSettings(val.ok);
      return val;
    }
  });

  constructor(session: Session) {
    super();
    this.session = session;
    this.request = new UserRequest(session);
    this.store = new UserStore();
    this.user = session.getManager(AuthenticationManager).getAuthentication()?.user;
    const authenticationManagerSubscription = session.getManager(AuthenticationManager).subscribe(event => {
      switch (event.type) {
        case 'authentication:session_update':
          if (!deepEqual(this.user, event.auth?.user)) {
            this.user = event.auth?.user;
            this.dispatch({
              type: 'user:user_update',
              user: event.auth?.user,
            });
          }
          break;
      }
    });
    session.onPreSessionClose(() => authenticationManagerSubscription.unsubscribe());
  }

  public static getName = () => 'benzinga-user';

  public updateAvatar = async (avatarPost: UserAvatarPost): SafePromise<UserAvatar> => {
    return await this.request.updateAvatar(avatarPost);
  };

  public updateProperty = async (email: string, property: string, value: string): SafePromise<any> => {
    return await this.request.updateProperty(email, property, value);
  };

  public getUser = (): User | undefined => this.user;

  public editUser = async (user: UserEditInfo): SafePromise<User> => {
    const res = await this.request.editUser(user);
    if (res.ok) {
      this.user = res.ok;
      this.dispatch({
        type: 'user:user_update',
        user: res.ok,
      });
    }
    return res;
  };

  public getUserAsync = (): Promise<User | undefined> => {
    return new Promise(resolve => {
      if (this.session.getManager(AuthenticationManager).isLoggedIn()) {
        const user = this.getUser();

        if (user !== undefined) {
          resolve(user);
        } else {
          const authSubscription = this.session.getManager(AuthenticationManager).subscribe(event => {
            if (event.type === 'authentication:session_update') {
              if (this.getUser()) {
                authSubscription.unsubscribe();
                resolve(this.getUser());
              }
            }
          });
        }
      } else {
        resolve(undefined);
      }
    });
  };

  public getGlobalSettings = async <T = object>(key?: string, force?: boolean): SafePromise<T | undefined | string> => {
    if (this.store.shouldUpdateGlobalSettings() || force) {
      await this.fetchGlobalSettings();
    }
    const globalSettings = this.store.getGlobalSettings();
    return { ok: key ? globalSettings?.[key] : globalSettings };
  };

  public getGlobalSettingsCached = <T = object>(key?: string): T | undefined => {
    const globalSettings = this.store.getGlobalSettings();
    return key ? globalSettings?.[key] : (globalSettings as T);
  };

  public getSegmentUserTraits = async (params?: {
    userId?: string;
    anonymousId?: string;
  }): SafePromise<Record<number, string | number | boolean>> => {
    const response = await this.request.getSegmentUserTraits(params);
    if (response.ok) {
      const traitsMap = {
        1: response.ok.traits?.first_created_at,
        3: response.ok.traits?.gender,
        4: response.ok.traits?.income,
        5: response.ok.traits?.age_group,
        6: response.ok.traits?.education,
        7: response.ok.traits?.employment_status,
        8: response.ok.traits?.user_stage,
        9: response.ok.traits?.has_subscriptions,
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        10: response.ok.traits?.active_subscriptions,
        11: response.ok.traits?.active_plan_ids,
        12: response.ok.traits?.active_pro_subscriber,
        13: response.ok.traits?.active_edge_subscriber,
        14: response.ok.traits?.active_research_subscriber,
        15: response.ok.traits?.total_subscriptions,
      } as Record<number, string | number | boolean>;
      return { ok: traitsMap };
    }
    return response;
  };

  public setGlobalSetting = async (key: string, value: unknown): SafePromise<undefined> => {
    const val = await this.request.setGlobalSettings(key, value);
    if (val.err) {
      return val;
    } else {
      this.store.setGlobalSettings({ ...(this.store.getGlobalSettings() ?? {}), [key]: value });
      return val;
    }
  };

  public setUserTraderSurvey = async (survey: UserTraderSurvey): SafePromise<undefined> => {
    const val = await this.request.setGlobalSettings('trader_survey', survey);
    if (val.err) {
      return val;
    } else {
      this.store.setGlobalSettings({ ...(this.store.getGlobalSettings() ?? {}), trader_survey: survey });
      return val;
    }
  };

  public declareMarketInterest = (interest: string) => {
    return this.request.declareMarketInterest(interest);
  };

  public deleteAccount = (): SafePromise<unknown> => {
    return this.request.deleteAccount();
  };

  public getNotificationChannels = async (): SafePromise<NotificationChannelsResponse> => {
    return await this.request.getNotificationChannels();
  };

  public toggleSubscription = async (
    id: string | number,
    topicType: 'channel' | 'messageType',
  ): SafePromise<ToggleSubscriptionResponse> => {
    return await this.request.toggleSubscription(id, topicType);
  };

  public unsubscribeFromAllChannels = async (provider: 'iterable' | 'beehiiv'): SafePromise<{ message: string }> => {
    try {
      const channelsResponse = await this.getNotificationChannels();
      if (channelsResponse.err) {
        return channelsResponse;
      }

      const subscribed = channelsResponse.ok.channels.filter(c => c.provider === provider && c.is_subscribed);
      const togglePromises = subscribed.map(c => this.toggleSubscription(c.id, 'channel'));

      const results = await Promise.all(togglePromises);
      const failedToggles = results.filter(result => result.err);

      if (failedToggles.length > 0) {
        return {
          err: new SafeError(
            `Failed to unsubscribe from ${failedToggles.length} channels`,
            'unsubscribe_error',
            undefined,
          ),
        };
      }

      return {
        ok: {
          message: `Successfully unsubscribed from all ${provider} channels`,
        },
      };
    } catch (error) {
      return {
        err: new SafeError(
          error instanceof Error ? error.message : 'Network error occurred',
          'network_error',
          error instanceof Error ? error : undefined,
        ),
      };
    }
  };

  public captureTOSAcceptance = (tosAcceptance: TOSAcceptance): SafePromise<unknown> => {
    return this.request.captureTOSAcceptance(tosAcceptance);
  };

  protected onSubscribe(): UserFunctions {
    return {
      editUser: this.editUser,
      getNotificationChannels: this.getNotificationChannels,
      toggleSubscription: this.toggleSubscription,
      updateAvatar: this.updateAvatar,
    };
  }

  protected onFirstSubscription(): void {
    this.requestSubscription = this.request.subscribe(event => this.dispatch(event));
    this.storeSubscription = this.store.subscribe(event => this.dispatch(event));
  }

  protected onZeroSubscriptions(): void {
    this.requestSubscription?.unsubscribe();
    this.requestSubscription = undefined;
    this.storeSubscription?.unsubscribe();
    this.storeSubscription = undefined;
  }
}
